import { boot } from 'quasar/wrappers'
import { reactive, watch } from 'vue'

const STORAGE_KEY = 'dd_a11y_v1'

const defaultState = Object.freeze({
  fontScale: 1,
  highContrast: false,
  reduceMotion: false
})

function safeParse (value) {
  try {
    return JSON.parse(value)
  } catch (e) {
    return null
  }
}

function clamp (value, min, max) {
  return Math.min(max, Math.max(min, value))
}

function normalizeFontScale (value) {
  const asNumber = Number(value)
  if (!Number.isFinite(asNumber)) return defaultState.fontScale
  return clamp(asNumber, 0.9, 1.25)
}

function loadState () {
  if (typeof window === 'undefined') return { ...defaultState }

  const raw = window.localStorage?.getItem(STORAGE_KEY)
  if (!raw) return { ...defaultState }

  const parsed = safeParse(raw)
  if (!parsed || typeof parsed !== 'object') return { ...defaultState }

  return {
    fontScale: normalizeFontScale(parsed.fontScale),
    highContrast: !!parsed.highContrast,
    reduceMotion: !!parsed.reduceMotion
  }
}

function saveState (state) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify({
      fontScale: state.fontScale,
      highContrast: state.highContrast,
      reduceMotion: state.reduceMotion
    }))
  } catch (e) {
    // ignore storage failures (private mode, quota, etc.)
  }
}

function applyToDom (state) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.style.setProperty('--dd-font-scale', String(state.fontScale))

  if (document.body) {
    document.body.classList.toggle('dd-a11y-contrast', !!state.highContrast)
    document.body.classList.toggle('dd-a11y-reduce-motion', !!state.reduceMotion)
  }
}

export default boot(({ app }) => {
  const state = reactive(loadState())

  const api = {
    state,

    set (patch = {}) {
      if (Object.prototype.hasOwnProperty.call(patch, 'fontScale')) {
        state.fontScale = normalizeFontScale(patch.fontScale)
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'highContrast')) {
        state.highContrast = !!patch.highContrast
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'reduceMotion')) {
        state.reduceMotion = !!patch.reduceMotion
      }
    },

    reset () {
      state.fontScale = defaultState.fontScale
      state.highContrast = defaultState.highContrast
      state.reduceMotion = defaultState.reduceMotion
    }
  }

  watch(state, () => {
    applyToDom(state)
    saveState(state)
  }, { deep: true, immediate: true })

  app.config.globalProperties.$a11y = api
  app.provide('a11y', api)
})
